import React, { useState, useEffect, useRef } from 'react';
import { Button, Row } from 'antd';
import { CloseIcon, DragIcon, AddImageIcon } from 'components/Icons';
import { ReactSortable } from 'react-sortablejs';
import PropTypes from 'prop-types';

/**
 * Manage images modal
 * @param {Object} props
 * @param {Function} props.onClosePane - On close pane
 * @param {Array} props.catalogThumbnails - Catalog thumbnails
 * @param {Function} props.setCatalogThumbnails - Set catalog thumbnails
 * @param {Function} props.setShowOrHidesUploadModal - Set show or hides upload modal
 */
const ManageImagesModal = ({
  onClosePane,
  catalogThumbnails,
  setCatalogThumbnails,
  setShowOrHidesUploadModal,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    // Trigger slide-in animation on mount
    // Use requestAnimationFrame to ensure browser has painted initial state first
    const rafId = requestAnimationFrame(() => {
      // Force a reflow to ensure initial state is rendered
      if (contentRef.current) {
        contentRef.current.offsetHeight;
      }
      // Then trigger the animation on the next frame
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before calling onClosePane
    setTimeout(() => {
      onClosePane();
    }, 300); // Match animation duration
  };

  return (
    <div className={`tabList-drawer manage-images-modal ${isClosing ? 'closing' : ''}`}>
      <div
        className={`ant-modal-mask ${isClosing ? 'fade-out' : isVisible ? 'fade-in' : ''}`}
        onClick={handleClose}></div>
      <div
        ref={contentRef}
        className={`content ${
          isVisible && !isClosing ? 'slide-left' : isClosing ? 'slide-right' : ''
        }`}>
        <div className="drawer-header">
          <h2>Manage Images</h2>
          <Button className="medium-btn grey-btn tabList-drawer-close" onClick={handleClose}>
            <CloseIcon />
          </Button>
        </div>
        <div className="body">
          <Row className="tablist-image-item">
            <ReactSortable
              list={catalogThumbnails}
              setList={(newState) => {
                if (JSON.stringify(catalogThumbnails) !== JSON.stringify(newState)) {
                  setCatalogThumbnails(newState);
                }
              }}
              animation={400}
              className="sortable-container"
              handle=".handle">
              {catalogThumbnails.map((thumbnail, thumbnailIndex) => (
                <div key={thumbnailIndex} className="catalog-add-product__image-container">
                  <div className="handle">
                    <DragIcon />
                    <DragIcon />
                  </div>
                  <CloseIcon
                    className="catalog-add-product__close-icon"
                    onClick={() => {
                      const tempCatalogThumbnails = catalogThumbnails.filter(
                        (image, idx) => !(image.url === thumbnail.url && idx === thumbnailIndex)
                      );
                      setCatalogThumbnails(tempCatalogThumbnails);
                    }}
                  />
                  <img className="catalog-add-product__image" src={thumbnail.url} alt="product" />
                </div>
              ))}
            </ReactSortable>
            <div className="catalog-add-product__upload-icon">
              <AddImageIcon onClick={() => setShowOrHidesUploadModal(true)} />
            </div>
          </Row>
        </div>
      </div>
    </div>
  );
};

ManageImagesModal.defaultProps = {
  catalogThumbnails: [],
  setCatalogThumbnails: () => {},
};

ManageImagesModal.propTypes = {
  onClosePane: PropTypes.func.isRequired,
  catalogThumbnails: PropTypes.array.isRequired,
  setCatalogThumbnails: PropTypes.func.isRequired,
  setShowOrHidesUploadModal: PropTypes.func.isRequired,
};

export default ManageImagesModal;
